import { useEffect, useState, useRef, useCallback } from "react";
import { fetchTicketThreadMessages } from "@/pages/tickets/api/ticketHistoryApi";
import { attachmentApi } from "@/api/AttachmentApiClient";
import MessageAttachments from "@/components/chat/MessageAttachments";
import AgentMessageAvatar from "@/components/chat/AgentMessageAvatar";

export default function TicketHistoricThreadModal({ open, ticketId, workspaceId, onClose, previewLabel = "" }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  const fetchDownloadUrl = useCallback(
    (attachmentId) =>
      attachmentApi.fetchDownloadUrl({
        attachmentId,
        workspaceId,
        context: "ticket",
      }),
    [workspaceId],
  );

  useEffect(() => {
    if (!open || !ticketId) {
      setMessages([]);
      setError("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const data = await fetchTicketThreadMessages({ ticketId, workspaceId });
        if (cancelled) return;
        const rows = Array.isArray(data?.data) ? data.data : [];
        setMessages(rows);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load ticket thread.");
          setMessages([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, ticketId, workspaceId]);

  useEffect(() => {
    if (open && messages.length) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [open, messages]);

  if (!open) return null;

  const _renderTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center p-3 sm:items-center sm:p-6" role="presentation">
      <button type="button" className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ticket-thread-title"
        className="relative z-10 flex h-[min(88vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/25 ring-1 ring-slate-900/[0.06]"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 bg-slate-50/90 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h2 id="ticket-thread-title" className="truncate text-base font-semibold text-slate-900">
              {loading ? "Loading..." : `Ticket ${ticketId}`}
            </h2>
            <p className="mt-0.5 truncate text-[11px] text-slate-500">
              {previewLabel ? `${previewLabel}` : "Historic ticket thread"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="ui-btn-icon !h-9 !w-9 shrink-0" aria-label="Close">
            <i className="ri-close-line text-lg" />
          </button>
        </header>

        <div className="scrollbar-thin min-h0 flex-1 space-y-3 overflow-y-auto bg-slate-50/30 px-3 py-4 sm:px-5 sm:py-5">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-slate-500">
              <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
              Loading thread...
            </div>
          )}
          {!loading && error && (
            <p className="rounded-lg border border-rose-200 bg-rose-50/90 px-3 py-2 text-sm text-rose-800">{error}</p>
          )}
          {!loading && !error && messages.length === 0 && <p className="text-center text-sm text-slate-500">No messages in this ticket.</p>}
          {!loading &&
            !error &&
            messages.map((m, index) => {
              const text = m?.body_text || "";
              const hideText = m?.attachments?.length && !text.trim();
              const displayText = text.trim() || (hideText ? "" : "(empty)");
              const sender = String(m?.sender_type || "");
              const isCustomer = sender === "customer";
              const rowKey =
                m?.id != null && String(m.id).trim()
                  ? String(m.id)
                  : `ticket-msg-${index}-${m?.created_at ?? ""}`;
              return (
                <div key={rowKey} className={isCustomer ? "flex items-end gap-2" : "flex items-end justify-end gap-2"}>
                  {isCustomer ? (
                    <>
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500">
                        <i className="ri-user-3-line text-xs" />
                      </div>
                      <div className="max-w-[min(80%,480px)]">
                        {displayText ? (
                          <div className="rounded-2xl rounded-bl-sm border border-slate-200/80 bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-800 shadow-sm">
                            {displayText}
                          </div>
                        ) : null}
                        {m?.attachments?.length ? (
                          <MessageAttachments
                            attachments={m.attachments}
                            fetchDownloadUrl={fetchDownloadUrl}
                            variant="user"
                          />
                        ) : null}
                        <p className="mt-0.5 pl-0.5 text-[10px] text-slate-400">{_renderTime(m?.created_at)}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="max-w-[min(80%,480px)]">
                        {displayText ? (
                          <div className="rounded-2xl rounded-br-sm bg-teal-700 px-3.5 py-2.5 text-sm text-white shadow-sm">
                            {displayText}
                          </div>
                        ) : null}
                        {m?.attachments?.length ? (
                          <MessageAttachments
                            attachments={m.attachments}
                            fetchDownloadUrl={fetchDownloadUrl}
                            variant="agent"
                          />
                        ) : null}
                        <p className="mt-0.5 pr-0.5 text-right text-[10px] text-slate-400">{_renderTime(m?.created_at)}</p>
                      </div>
                      <AgentMessageAvatar size="sm" />
                    </>
                  )}
                </div>
              );
            })}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
}
